import { PaymentDropInClassNames, PaymentEvents } from '@accedo/one-plugin-sdk';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { FormButton } from '#/components/Form/Form.styled';
import { PageSpinner } from '#/components/Splash/Splash.styled';
import { noop, remify } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import { PaymentFormProps } from './types';

interface ContainerProps {
  /** If the drop-in is at the very bottom of the view, add some extra bottom margin. */
  $isAtBottom?: boolean;
}

const Container = styled.div<ContainerProps>`
  position: relative;
  display: flex;
  justify-content: center;
  text-align: initial;
  min-height: 20rem;
  border-radius: ${remify(8)};
  overflow: hidden;
  margin-bottom: ${({ $isAtBottom }) => remify($isAtBottom ? 48 : 0)};
`;

const hiddenStyle = { display: 'none' };

export const DropInContainer = ({ isAtBottom }: { isAtBottom?: boolean }) => (
  <Container
    $isAtBottom={isAtBottom}
    className={PaymentDropInClassNames.Container}
    data-testid="drop-in-container"
  >
    <PageSpinner
      $startFadeout={false}
      className={PaymentDropInClassNames.Spinner}
    />
    <FormButton
      className={PaymentDropInClassNames.TemplateButton}
      style={hiddenStyle}
    >
      {tb('web.iap.purchase.button')}
    </FormButton>
  </Container>
);

export const PaymentDropInForm = ({
  setIsDisabled = noop,
  onPurchase,
  onSuccess,
  onFailure,
}: PaymentFormProps) => {
  const isFirstRender = useRef(true);

  useEffect(() => {
    const onSubmit = () => setIsDisabled(true);
    document.addEventListener(PaymentEvents.DropInSubmitStart, onSubmit);

    return () =>
      document.removeEventListener(PaymentEvents.DropInSubmitStart, onSubmit);
  }, [setIsDisabled]);

  useEffect(() => {
    // no matter what dependency has changed, we only want to start this once.
    if (!isFirstRender.current) {
      return;
    }
    isFirstRender.current = false;

    onPurchase({})
      .then(onSuccess)
      .catch(onFailure)
      .finally(() => setIsDisabled(false));
  }, [setIsDisabled, onFailure, onPurchase, onSuccess]);

  return <DropInContainer isAtBottom />;
};
